import React, { useState } from "react";
import { FaTimes, FaEdit } from "react-icons/fa";

const STATUSES = [
  "OPEN",
  "ASSIGNED",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
  "REJECTED",
];

const statusColors = {
  OPEN: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-indigo-100 text-indigo-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  RESOLVED: "bg-green-100 text-green-700",
  CLOSED: "bg-gray-200 text-gray-700",
  REJECTED: "bg-red-100 text-red-700",
};

const priorityColors = {
  LOW: "bg-gray-100 text-gray-700",
  MEDIUM: "bg-blue-100 text-blue-700",
  HIGH: "bg-orange-100 text-orange-700",
  CRITICAL: "bg-red-100 text-red-700",
};

const formatLabel = (value = "") =>
  value
    .toString()
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (date) => {
  if (!date) return "—";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export default function TicketViewModal({
  ticket,
  onClose,
  onEdit,
  apiRequest,
  onUpdateSuccess,
}) {
  const [remarksDraft, setRemarksDraft] = useState(ticket?.remarks || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const id = ticket?._id || ticket?.id;

  const updateStatus = async (status) => {
    if (!id) return;
    setSaving(true);
    setError("");
    try {
      await apiRequest(`returns/update-ticket?id=${id}`, {
        method: "PUT",
        body: JSON.stringify({ status }),
      });
      onUpdateSuccess(id, { status }, `Marked ${formatLabel(status).toLowerCase()}.`);
    } catch (err) {
      setError(err.message || "Unable to update ticket status.");
    } finally {
      setSaving(false);
    }
  };

  const saveRemarks = async () => {
    if (!id) return;
    setSaving(true);
    setError("");
    try {
      await apiRequest(`returns/update-ticket?id=${id}`, {
        method: "PUT",
        body: JSON.stringify({ remarks: remarksDraft }),
      });
      onUpdateSuccess(id, { remarks: remarksDraft }, "Remarks saved.");
    } catch (err) {
      setError(err.message || "Unable to save remarks.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Ticket Details</h2>
            <p className="mt-1 text-sm text-gray-500">{ticket?.ticket_number || "Ticket"}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
            <FaTimes />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-5 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Ticket Number</p>
              <p className="mt-1 font-semibold text-gray-900">{ticket?.ticket_number || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Ticket Type</p>
              <p className="mt-1 text-gray-800">{formatLabel(ticket?.ticket_type || "")}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Priority</p>
              <p className="mt-1">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${priorityColors[ticket?.priority] || ""}`}>
                  {formatLabel(ticket?.priority || "—")}
                </span>
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Current Status</p>
              <p className="mt-1">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusColors[ticket?.status] || ""}`}>
                  {formatLabel(ticket?.status || "—")}
                </span>
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Related Entity</p>
              <p className="mt-1 text-gray-800">
                {ticket?.entity_type ? `${formatLabel(ticket.entity_type)} · ${ticket.entity_id || "No ID"}` : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500">Created</p>
              <p className="mt-1 text-gray-800">{formatDate(ticket?.createdAt || ticket?.created_at)}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase text-gray-500">Description</p>
            <p className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
              {ticket?.description || "No description"}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">Remarks / Internal Notes</label>
            <textarea
              rows={4}
              value={remarksDraft}
              onChange={(e) => setRemarksDraft(e.target.value)}
              placeholder="Add investigation notes or updates..."
              className="w-full rounded-lg border border-gray-300 p-2.5 text-sm outline-none focus:border-blue-500"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={saveRemarks}
                disabled={saving}
                className="rounded-lg bg-[#f7525a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#f7525a] disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Remarks"}
              </button>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">Update Status</label>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((status) => (
                <button
                  key={status}
                  disabled={saving || ticket?.status === status}
                  onClick={() => updateStatus(status)}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    ticket?.status === status
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {formatLabel(status)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <FaEdit />
              Edit Ticket
            </button>
            <button
              onClick={onClose}
              className="rounded-lg bg-[#f7525a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#f7525a]"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}